import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import { DeleteForever } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    dataGridNestedObjectValueGetter,
    PourFormatInput,
} from '@barboxx/admin-components';
import {
    CREATE_POUR_AVAILABILITY_SCHEMA,
    ID_WIDTH,
    NAME_MIN_WIDTH,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import {
    PourAvailability,
    PourFormat,
    PourFormatDetails,
    StandardDrink,
} from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';

const COLUMNS: GridColDef<PourFormat>[] = [
    {
        field: 'id',
        headerName: 'Id',
        width: ID_WIDTH,
    },

    {
        field: 'pourType.name',
        headerName: 'Pour Type',
        minWidth: NAME_MIN_WIDTH,
        valueGetter: dataGridNestedObjectValueGetter,
        flex: 1,
    },

    {
        field: 'servingContainer.name',
        headerName: 'Serving Container',
        minWidth: NAME_MIN_WIDTH,
        valueGetter: dataGridNestedObjectValueGetter,
        flex: 1,
    },

    {
        field: 'productCategory.name',
        headerName: 'Alcohol Category',
        minWidth: NAME_MIN_WIDTH,
        valueGetter: dataGridNestedObjectValueGetter,
    },
    {
        field: 'volumeOz',
        headerName: 'Volume (oz)',
        minWidth: NAME_MIN_WIDTH,
    },
    {
        field: 'displayOrder',
        headerName: 'Display Order',
        minWidth: NAME_MIN_WIDTH,
    },
    {
        field: 'isDefault',
        headerName: 'Is Default',
        minWidth: NAME_MIN_WIDTH,
        type: 'boolean',
    },
];

interface Props {
    standardDrink: StandardDrink.DetailsResponse;
    refetch: () => void;
}

export const PourFormatListCard = ({ standardDrink, refetch }: Props) => {
    const { id, pourFormats } = standardDrink;
    const navigate = useNavigate();
    const notifications = useNotifications();

    const [open, dialogActions] = useBool();

    const formContext = useForm<PourAvailability.CreateRequest>({
        resolver: yupResolver(CREATE_POUR_AVAILABILITY_SCHEMA),
        defaultValues: {
            standardDrinkId: id,
            entityType: 'Drink',
            isDefault: false,
        },
    });

    const mutateCreatePourFormat = useMutation({
        mutationKey: ['pourAvailability'],
        mutationFn: (data: PourAvailability.CreateRequest) =>
            Api.Menu.PourAvailability.postPourAvailability(data),
    });

    const onSubmit = async (values: PourAvailability.CreateRequest) => {
        return mutateCreatePourFormat
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created  pour format!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating pour format!', {
                    severity: 'error',
                });
            });
    };

    const mutateDeletePourAvailability = useMutation({
        mutationKey: ['deletePourAvailability'],
        mutationFn: (data: PourAvailability.DeleteRequest) =>
            Api.Menu.PourAvailability.deletePourAvailability(data),
    });

    const handleDeletePourAvailability = async (
        values: PourAvailability.DeleteRequest
    ) => {
        return mutateDeletePourAvailability
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Deleted pour availability!`, {
                    severity: 'success',
                });
                refetch();
            })
            .catch(e => {
                notifications.show('Error deleting pour availability!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (params: GridRowParams<PourFormatDetails>) => {
        navigate(`/core/pour-formats/${params.row.id}`);
    };

    const columns = COLUMNS.concat([
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: NAME_MIN_WIDTH,
            type: 'actions',
            getActions: params => [
                // <Button
                //     key="edit"
                //     onClick={() => {
                //         navigate(`${params.id}`);
                //     }}
                // >
                //     Edit
                // </Button>,
                <IconButton
                    key="delete"
                    onClick={() => {
                        handleDeletePourAvailability(params.row);
                        // Handle delete action
                    }}
                >
                    <DeleteForever />
                </IconButton>,
            ],
        },
    ]);

    return (
        <>
            <DataGridCard
                title="Pour Formats List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Format
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={pourFormats}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Pour Format
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <PourFormatInput />
                        <TextFieldElement
                            label="Display Order"
                            name="displayOrder"
                            fullWidth
                        />
                        <CheckboxElement label="Is Default" name="isDefault" />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
